import React, { useState } from 'react';
import { FaFileInvoiceDollar, FaDownload, FaFilter, FaChartBar } from 'react-icons/fa';
import PaymentsList from './PaymentsList';

type DateRange = {
  startDate: string;
  endDate: string;
};

type PaymentStatus = 'all' | 'paid' | 'pending' | 'refunded';

const PaymentsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('all');
  
  // Mock data for the report summary
  const reportSummary = {
    totalRevenue: 12500,
    paidBookings: 25,
    pendingPayments: 5,
    refunds: 2,
    averageBookingValue: 500
  };
  
  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaymentStatus(e.target.value as PaymentStatus);
  };
  
  const handleExportReport = () => {
    // In a real app, this would generate and download a report
    alert('Exporting report for the selected date range and status...');
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <FaFileInvoiceDollar className="mr-2 text-blue-600" />
          Payments Report
        </h2>
        
        <button
          onClick={handleExportReport}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <FaDownload className="mr-2" />
          Export Report
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <div className="flex items-center space-x-2">
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateRangeChange}
              className="border rounded-md px-3 py-2 w-full"
            />
            <span>to</span>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateRangeChange}
              className="border rounded-md px-3 py-2 w-full"
            />
          </div>
        </div>
        
        <div className="w-full md:w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
          <select
            value={paymentStatus}
            onChange={handleStatusChange}
            className="border rounded-md px-3 py-2 w-full"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        
        <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md flex items-center">
          <FaFilter className="mr-2" />
          Apply Filters
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Revenue</div>
          <div className="text-xl font-bold">RM {reportSummary.totalRevenue.toLocaleString()}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Paid Bookings</div>
          <div className="text-xl font-bold">{reportSummary.paidBookings}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Pending Payments</div>
          <div className="text-xl font-bold">{reportSummary.pendingPayments}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Refunds</div>
          <div className="text-xl font-bold">{reportSummary.refunds}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Avg. Booking Value</div>
          <div className="text-xl font-bold">RM {reportSummary.averageBookingValue}</div>
        </div>
      </div>
      
      {/* Chart Placeholder */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center mb-4">
          <FaChartBar className="text-blue-600 mr-2" />
          <h3 className="text-lg font-medium">Revenue Trend</h3>
        </div>
        <div className="h-64 bg-gray-100 rounded flex items-center justify-center">
          <p className="text-gray-500">Chart visualization would appear here</p>
        </div>
      </div>
      
      {/* Payments List */}
      <div className="bg-white rounded-lg shadow">
        <PaymentsList 
          payments={[]}
          isLoading={false}
          onSelect={() => {}}
          onGenerateInvoice={() => {}}
        />
      </div>
    </div>
  );
};

export default PaymentsReport;
